import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/shared/SEO';

/**
 * About Page
 */
export default function About() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-midnight min-h-screen">
      <SEO
        title="About Us"
        description="Raflr is Ireland's skill-based competition marketplace for local pickup items. Win amazing deals on electronics, gaming, furniture and more."
        path="/about"
      />
      <div className="page-container py-12 md:py-20">
        <div className="max-w-3xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-3xl md:text-5xl font-display text-cream mb-6">
              About Raflr
            </h1>
            <p className="text-lg md:text-xl text-cream/70 max-w-2xl mx-auto">
              Ireland's skill-based competition marketplace for local pickup items.
              Win amazing deals. Sell with confidence.
            </p>
          </div>

          {/* Mission */}
          <section className="mb-16">
            <h2 className="text-2xl font-semibold text-cream mb-4">Our Mission</h2>
            <p className="text-cream/70 mb-4">
              Raflr connects buyers and sellers through skill-based competitions, creating a fun and fair way to acquire quality items at great prices while giving sellers a new channel to reach eager buyers.
            </p>
            <p className="text-cream/70">
              Unlike traditional classified ads where items sit for weeks, Raflr creates excitement and urgency. Sellers set their price, buyers enter for a chance to win, and everyone gets a fair shot.
            </p>
          </section>

          {/* How It Works - Brief */}
          <section className="mb-16">
            <h2 className="text-2xl font-semibold text-cream mb-6">How It Works</h2>

            <div className="grid gap-6 md:grid-cols-3">
              <div className="bg-midnight-light border border-cream/10 rounded-lg p-6">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center mb-4">
                  <span className="text-2xl">1</span>
                </div>
                <h3 className="text-lg font-semibold text-cream mb-2">Browse & Enter</h3>
                <p className="text-cream/60 text-sm">
                  Find items you love, answer a skill question, and enter for a chance to win.
                </p>
              </div>

              <div className="bg-midnight-light border border-cream/10 rounded-lg p-6">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center mb-4">
                  <span className="text-2xl">2</span>
                </div>
                <h3 className="text-lg font-semibold text-cream mb-2">Win & Collect</h3>
                <p className="text-cream/60 text-sm">
                  Winners are selected when competitions end. Arrange local pickup with the seller.
                </p>
              </div>

              <div className="bg-midnight-light border border-cream/10 rounded-lg p-6">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center mb-4">
                  <span className="text-2xl">3</span>
                </div>
                <h3 className="text-lg font-semibold text-cream mb-2">Safe & Secure</h3>
                <p className="text-cream/60 text-sm">
                  Payments held in escrow until both parties confirm. Full refunds if competitions don't complete.
                </p>
              </div>
            </div>

            <div className="mt-6 text-center">
              <Link to="/how-it-works" className="text-primary hover:underline">
                Learn more about how Raflr works →
              </Link>
            </div>
          </section>

          {/* Why Raflr */}
          <section className="mb-16">
            <h2 className="text-2xl font-semibold text-cream mb-6">Why Raflr?</h2>

            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-success/20 flex items-center justify-center">
                  <svg className="w-5 h-5 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-cream mb-1">Skill-Based, Not Luck</h3>
                  <p className="text-cream/60 text-sm">
                    Every entry requires answering a skill question correctly. This isn't gambling—it's a legitimate competition where knowledge matters.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-success/20 flex items-center justify-center">
                  <svg className="w-5 h-5 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-cream mb-1">Local Pickup Only</h3>
                  <p className="text-cream/60 text-sm">
                    No shipping hassles. Meet locally in Ireland to collect your prize. See items in person before completing the handover.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-success/20 flex items-center justify-center">
                  <svg className="w-5 h-5 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-cream mb-1">Protected Payments</h3>
                  <p className="text-cream/60 text-sm">
                    Funds are held securely until both buyer and seller confirm the handover. If something goes wrong, we've got you covered.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-success/20 flex items-center justify-center">
                  <svg className="w-5 h-5 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-cream mb-1">Fair Pricing</h3>
                  <p className="text-cream/60 text-sm">
                    Free to list. We only take 5% when you succeed - covers payment processing, escrow, and dispute handling. You keep 95%.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-success/20 flex items-center justify-center">
                  <svg className="w-5 h-5 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-cream mb-1">Verified Users</h3>
                  <p className="text-cream/60 text-sm">
                    Phone verification required for all users. Seller identity verified through Stripe. Build trust through reviews.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* For Sellers */}
          <section className="mb-16">
            <h2 className="text-2xl font-semibold text-cream mb-4">For Sellers</h2>
            <p className="text-cream/70 mb-4">
              Raflr offers a new way to sell items that creates excitement and guarantees you reach your asking price. Set your minimum threshold, and if it's not met, the competition cancels and buyers are refunded automatically.
            </p>
            <ul className="list-disc list-inside text-cream/70 space-y-2 ml-4 mb-6">
              <li>List in minutes with our easy wizard</li>
              <li>AI-powered pricing suggestions</li>
              <li>Get paid directly to your bank account</li>
              <li>Free to list, 5% fee only on successful sales</li>
              <li>In-app messaging with winners</li>
            </ul>
            <Link
              to="/create"
              className="inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-midnight font-semibold px-6 py-3 rounded-lg transition-colors"
            >
              Start Selling
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </section>

          {/* Trust & Safety */}
          <section className="mb-16">
            <h2 className="text-2xl font-semibold text-cream mb-4">Trust & Safety</h2>
            <p className="text-cream/70 mb-4">
              Your safety is our priority. We've built multiple layers of protection:
            </p>
            <ul className="list-disc list-inside text-cream/70 space-y-2 ml-4">
              <li>Phone verification prevents fake accounts</li>
              <li>Seller identity verified through Stripe</li>
              <li>Escrow payments protect both parties</li>
              <li>Dispute resolution system for any issues</li>
              <li>User reviews build community trust</li>
              <li>Entry limits prevent manipulation</li>
            </ul>
          </section>

          {/* Legal */}
          <section className="mb-16">
            <h2 className="text-2xl font-semibold text-cream mb-4">Legal Status</h2>
            <p className="text-cream/70 mb-4">
              Raflr operates as a skill-based competition platform, not a lottery or gambling service. Under Irish law, competitions that require genuine skill to enter are legal prize competitions.
            </p>
            <p className="text-cream/70">
              Every entry on Raflr requires correctly answering a skill question, ensuring our competitions meet the legal definition of skill-based prize competitions.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-midnight-light border border-cream/10 rounded-lg p-8">
            <h2 className="text-2xl font-semibold text-cream mb-4">Get in Touch</h2>
            <p className="text-cream/70 mb-6">
              Have questions, feedback, or need support? We'd love to hear from you.
            </p>
            <div className="space-y-3">
              <p className="text-cream/70">
                <strong className="text-cream">General enquiries:</strong>{' '}
                <a href="mailto:hello@raflr.ie" className="text-primary hover:underline">hello@raflr.ie</a>
              </p>
              <p className="text-cream/70">
                <strong className="text-cream">Support:</strong>{' '}
                <a href="mailto:support@raflr.ie" className="text-primary hover:underline">support@raflr.ie</a>
              </p>
              <p className="text-cream/70">
                <strong className="text-cream">Privacy:</strong>{' '}
                <a href="mailto:privacy@raflr.ie" className="text-primary hover:underline">privacy@raflr.ie</a>
              </p>
            </div>
          </section>

          {/* Links */}
          <div className="mt-12 pt-8 border-t border-cream/10 flex flex-wrap gap-6">
            <Link to="/terms" className="text-cream/50 hover:text-cream transition-colors">
              Terms & Conditions
            </Link>
            <Link to="/privacy" className="text-cream/50 hover:text-cream transition-colors">
              Privacy Policy
            </Link>
            <Link to="/how-it-works" className="text-cream/50 hover:text-cream transition-colors">
              How It Works
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
